/** Base percentual. */
const PERCENT = 100;

/**
 * Valor a cobrar da parcela, derivado na tela.
 *
 * Usa **apenas** `totalDiscount` — o desconto consolidado que o operador digita —
 * e não a soma dos três percentuais. Pontualidade e retroativo têm fórmula do
 * backend e podem já estar contidos no total; somá-los produziria um número menor
 * que o real, e um valor errado exibido com aparência de cálculo é pior que
 * nenhum. É a mesma disciplina do resumo da aba Valores da edição, que
 * deliberadamente não recomputa o que o backend calcula.
 *
 * A multa fica fora de propósito: ela incide após o vencimento, não na criação.
 *
 * @param total - Valor total da parcela, como digitado.
 * @param totalDiscount - Desconto total em percentual (`10` para 10%).
 * @returns Valor a cobrar, ou `null` quando não há valor total para derivar de.
 *
 * @example
 * ```ts
 * deriveParcelChargeValue(1180, 10); // 1062
 * deriveParcelChargeValue(1180, undefined); // 1180
 * deriveParcelChargeValue(null, 10); // null
 * ```
 */
export function deriveParcelChargeValue(
  total: number | null | undefined,
  totalDiscount: number | null | undefined,
): number | null {
  if (total === null || total === undefined || Number.isNaN(total)) return null;

  if (totalDiscount === null || totalDiscount === undefined || Number.isNaN(totalDiscount)) {
    return total;
  }

  // Percentual fora da faixa já é recusado pelo schema; aqui a proteção evita
  // exibir valor negativo durante a digitação, antes da validação rodar.
  const clamped = Math.min(Math.max(totalDiscount, 0), PERCENT);

  return total - (total * clamped) / PERCENT;
}
